import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Col, Form, Row, Button, Container, Spinner } from "react-bootstrap";
import { v4 as uuidv4 } from "uuid";
import "./WorkItemRemake.css";

import { toast } from "react-toastify";
import moment from "moment";
import { useProperty } from "../../data/PropertyHook";
import { useWorkOrders } from "../../data/WorkOrdersHook";
import useLogger from "../../util/Logger";
import { useAppointmentSlots } from "../../data/AppointmentSlotHook";
import { useWorkItemAgreements } from "../../data/WorkItemAgreementsHook";
import { useWorkItemCategories } from "../../data/WorkItemCategoriesHook";
import { useProjects } from "../../data/ProjectsHook";
import { useBreadcrumb } from "../../data/BreadcrumbHook";
import { useAuth } from "../../data/AuthHook";
import {
  isWorkOrderClosed,
  isWorkOrderItemClosed,
} from "../../data/WorkOrderStatus";
import WorkOrderAddress from "../workOrder/WorkOrderAddress";
import { useWorkOrderItems } from "../../data/WorkItemHook";
import WorkItemEdit from "./WorkItemEdit";
import { useWorkItemTemplates } from "../../data/WorkItemTemplatesHook";
import SpinnerButton from "../shared/Spinnerbutton";
import { REMAKE_CANCELLATION_REASONS } from "../../util/CancellationReasons";

function WorkItemRemake() {
  const { propertyId, workOrderId, workItemId } = useParams();
  const propertyRepository = useProperty();
  const [property, setProperty] = useState(null);
  const [workOrder, setWorkOrder] = useState(null);
  const [validated, setValidated] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const workOrdersRepository = useWorkOrders();
  const auth = useAuth();
  const cancelledBy = `${auth?.user?.idToken?.payload?.given_name ?? ""} ${auth?.user?.idToken?.payload?.family_name ?? ""}`.trim();
  const [idempotencyGuid, setIdempotencyGuid] = useState();
  const workItemsRepository = useWorkOrderItems();
  const logger = useLogger();
  const appointmentSlotsRepository = useAppointmentSlots();
  const workItemAgreementsRepository = useWorkItemAgreements();
  const workItemCategoryRepository = useWorkItemCategories();
  const breadcrumbRepository = useBreadcrumb();
  const workItemTemplatesRepository = useWorkItemTemplates();
  const navigate = useNavigate();
  const projectsRepository = useProjects();

  useEffect(() => {
    const runMe = async () => {
      try {
        logger.debug("WorkItemRemake.useEffect", `Setting breadcrumb`);
        breadcrumbRepository.setBreadcrumb([
          { route: "/", name: "search" },
          { route: `/property/${propertyId}`, name: "property" },
          {
            route: `/property/${propertyId}/workorder/${workOrderId}`,
            name: "work order",
          },
          {
            route: `/property/${propertyId}/workOrder/${workOrderId}/workItem/${workItemId}/remake`,
            name: "remake work item",
          },
        ]);

        const [
          retrievedProperty,
          appointmentSlots,
          workItemCategories,
          templates,
        ] = await Promise.all([
          propertyRepository.getProperty(propertyId),
          appointmentSlotsRepository.updateAppointmentSlots(),
          workItemCategoryRepository.updateWorkItemCategories(),
          workItemTemplatesRepository.getWorkItemTemplates([
            "DomesticFieldReactive",
            "DomesticCMV",
            "Disconnection",
            "Reconnection",
            "DomesticProject-PropertyAsset",
            "DomesticProject-PropertyInstallation",
            "DomesticProject-PropertyOptimiseException",
            "DomesticProject-PropertyMeterException",
          ]),
          workItemAgreementsRepository.updateWorkItemAgreements(),
          projectsRepository.updateProjects(),
        ]);

        setProperty(retrievedProperty);

        const defaultAppointmentSlot = appointmentSlots.find(
          (appointmentSlot) => appointmentSlot.id === "PATS001"
        );

        const retrievedWorkOrder =
          await workOrdersRepository.getWorkOrderForUpdate(
            propertyId,
            workOrderId,
            workItemCategories
          );

        if (!retrievedWorkOrder)
          throw new Error(
            `Failed to load work order for property '${retrievedProperty.id}' and work order '${workOrderId}'`
          );

        if (isWorkOrderClosed(retrievedWorkOrder)) {
          throw new Error(
            `Illegal attempt to edit work item for closed work order for property '${retrievedProperty.id}' and work order '${workOrderId}'`
          );
        }

        const workItem = retrievedWorkOrder.workOrderItems.find(
          (element) => element.id === workItemId
        );
        if (!workItem)
          throw new Error(
            `Failed to load work order item for property '${retrievedProperty.id}' and work order '${workOrderId} and work order item '${workItem}'`
          );

        if (isWorkOrderItemClosed(workItem)) {
          throw new Error(
            `Illegal attempt to edit closed work item for property '${retrievedProperty.id}' and work order '${workOrderId}' and work order item '${workItem}'`
          );
        }

        const template = templates.find(
          (t) => t.name === workItem.workitemtemplatedisplayname
        );

        if (!template) {
          throw new Error(
            `Illegal attempt to edit disallowed work item template for property '${
              retrievedProperty.id
            }' and work order '${workOrderId}' and work order item '${JSON.stringify(
              workItem
            )}'`
          );
        }

        setWorkOrder(retrievedWorkOrder);
        workOrdersRepository.setEntry(retrievedWorkOrder);

        // Set the work item
        // eslint-disable-next-line no-console
        console.log("retrievedWorkOrder", retrievedWorkOrder);
        const workOrderItem = retrievedWorkOrder.workOrderItems.find(
          (element) => element.id === workItemId
        );

        // eslint-disable-next-line no-console
        console.log("workOrderItem", workOrderItem);

        workItemsRepository.setEntry({
          ...workOrderItem,
          workitemtemplateref: template.ref,
          preferredAppointmentSlot:
            workOrderItem.preferredAppointmentSlot || defaultAppointmentSlot.id,
          preferredAppointmentSlotTitle:
            workOrderItem.preferredAppointmentSlotTitle ||
            defaultAppointmentSlot.name,
        });

        setIsLoading(false);
      } catch (err) {
        logger.error(
          "WorkItemRemake.useEffect",
          `An error occurred while loading work order '${workOrderId}' ${err}`
        );
        toast.error(
          "An error occurred while loading the work item. Please try again."
        );
        navigate("/");
      }
    };
    runMe();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIdempotencyGuid(uuidv4());
  }, []);

  const handleRemake = async (event) => {
    try {
      setIsSaving(true);

      const form = event.currentTarget;
      event.preventDefault();
      event.stopPropagation();

      const current = workItemsRepository.getEntry();

      if (form.checkValidity() === false) {
        setValidated(true);
        toast.error(
          "There are errors on your form. Please check each field and try again."
        );
      } else if (
        !current.preferredAppointmentDate &&
        current.workitemtemplateref !== "Disconnection"
      ) {
        setValidated(true);
        toast.error("Please select a date from the calendar.");
      } else {
        // Save the work order item
        await workItemsRepository.createNewWorkOrderItem(workOrder, {
          ...current,
          idempotencyGuid,
        });

        await workItemsRepository.cancelWorkItem(
          current.id,
          current.workitemtemplateid,
          cancelReason,
          cancelledBy,
          current.subCategory
        );

        toast.success("The Work Item was remade successfully.");
        navigate(`/property/${propertyId}/workOrder/${workOrderId}`);
      }
    } catch (err) {
      logger.error(
        "WorkItemRemake.handleRemake",
        `API patch work item failed ${err}`
      );
      toast.error("The work item could not be remade. Please try again.");
    }
    setIsSaving(false);
  };

  const workOrderItem = workItemsRepository.getEntry();
  return !isLoading && workOrder && workOrderItem ? (
    <Container className="coreContent">
      <h2>Remake Work Item</h2>
      <WorkOrderAddress property={property} />

      <Row className="mt-4">
        <Col xs={3}>
          <Form.Label className="form-label-heading">
            Work Order Reference
          </Form.Label>
        </Col>
        <Col xs={3}>
          <Form.Label className="form-label-heading">Created</Form.Label>
        </Col>
        <Col xs={3}>
          <Form.Label className="form-label-heading">Type</Form.Label>
        </Col>
        <Col xs={3}>
          <Form.Label className="form-label-heading">Category</Form.Label>
        </Col>
      </Row>
      <Row className="mt-1">
        <Col xs={3}>
          <Form.Control type="text" readOnly value={workOrder.reference} />
        </Col>
        <Col xs={3}>
          <Form.Control
            type="text"
            readOnly
            value={
              workOrder.createdOn
                ? moment(workOrder.createdOn).format("DD/MM/YYYY h:mm a")
                : ""
            }
          />
        </Col>
        <Col xs={3}>
          <Form.Control type="text" readOnly value={workOrder.type} />
        </Col>
        <Col xs={3}>
          <Form.Control type="text" readOnly value={workOrder.categoryName} />
        </Col>
      </Row>

      <Form noValidate validated={validated} onSubmit={handleRemake}>
      
        <div className="entry-container">
          <WorkItemEdit editing />
        </div>
          <Form.Group controlId="work-item-remake-cancel-reason" className="mb-4">
          <Form.Label>Cancellation Reason</Form.Label>
          <Form.Select
            required
            value={cancelReason}
            onChange={(event) => setCancelReason(event.target.value)}
          >
            <option value="">Select a reason</option>
            {REMAKE_CANCELLATION_REASONS.map((reason) => (
              <option key={reason} value={reason}>
                {reason}
              </option>
            ))}
          </Form.Select>
          <Form.Control.Feedback type="invalid">
            Select a cancellation reason.
          </Form.Control.Feedback>
        </Form.Group>

        <div className="work-item-remake-buttons-container">
          <Row xs="auto">
            <Col className="work-item-remake-button">
              <Link to={`/property/${property.id}/workorder/${workOrderId}`}>
                <Button variant="secondary">Back to work order</Button>
              </Link>
            </Col>
            <Col>
              <SpinnerButton
                text="Remake"
                variant="primary"
                action={() => document.getElementById("remakeSubmit").click()}
                waiting={isSaving}
              />
            </Col>
            <Col>
              <Button
                id="remakeSubmit"
                type="submit"
                style={{ visibility: "hidden" }}
              >
                Save
              </Button>
            </Col>
          </Row>
        </div>
      </Form>
    </Container>
  ) : (
    <Container className="coreContent">
      <Spinner
        animation="border"
        role="status"
        style={{
          marginTop: "200px",
          marginLeft: "50%",
          marginBottom: "200px",
        }}
      >
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    </Container>
  );
}

export default WorkItemRemake;
